import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/common/ScrollToTop";
import Loader from "@/components/common/Loader";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { ShortlistProvider } from "@/context/ShortlistContext";
import ShortlistFAB from "@/components/features/ShortlistFAB";

// Lazy-loaded routes for performance optimization (Code Splitting)
const Home = lazy(() => import("@/pages/marketing/Home"));
const Directory = lazy(() => import("@/pages/surgeons/Directory"));
const SurgeonProfile = lazy(() => import("@/pages/surgeons/SurgeonProfile"));
const Login = lazy(() => import("@/pages/auth/Login"));
const Register = lazy(() => import("@/pages/auth/Register"));
const Dashboard = lazy(() => import("@/pages/dashboard/Dashboard"));
const About = lazy(() => import("@/pages/marketing/About"));
const Membership = lazy(() => import("@/pages/marketing/Membership"));
const Pricing = lazy(() => import("@/pages/marketing/Pricing"));
const Procedures = lazy(() => import("@/pages/procedures/Procedures"));
const ProcedureDetail = lazy(() => import("@/pages/procedures/ProcedureDetail"));
const AuthCallback = lazy(() => import("@/pages/auth/AuthCallback"));
const News = lazy(() => import("@/pages/news/News"));
const NewsDetail = lazy(() => import("@/pages/news/NewsDetail"));
const Privacy = lazy(() => import("@/pages/legal/Privacy"));
const Terms = lazy(() => import("@/pages/legal/Terms"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/auth/VerifyEmail"));
const Onboarding = lazy(() => import("@/pages/auth/Onboarding"));
const Brand = lazy(() => import("@/pages/marketing/Brand"));
const Admin = lazy(() => import("@/pages/admin/Admin"));
const Suspended = lazy(() => import("@/pages/auth/Suspended"));
const Compare = lazy(() => import("@/pages/surgeons/Compare"));

const AUTH_PATHS = [
  "/login", "/register", "/auth/callback", "/auth", "/forgot-password", 
  "/reset-password", "/verify-email", "/onboarding", "/suspended", "/admin"
];

function Layout({ children }) {
  const location = useLocation();
  const { loading } = useAuth();
  const isAuth = AUTH_PATHS.some((p) =>
    p === location.pathname || location.pathname.startsWith(p + "/")
  );
  return (
    <div className="min-h-screen flex flex-col bg-fscs-bg">
      {loading && <Loader variant="splash" label="Preparing your experience" />}
      {!isAuth && <Navbar />}
      <main className="flex-1">{children}</main>
      {!isAuth && <Footer />}
      {!isAuth && <ShortlistFAB />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ShortlistProvider>
            <AuthProvider>
              <ScrollToTop />
              <Toaster position="top-right" richColors closeButton />
              <Layout>
                <Suspense fallback={<Loader variant="splash" label="Loading..." />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/directory" element={<Directory />} />
                    <Route path="/surgeons/:id" element={<SurgeonProfile />} />
                    <Route path="/compare" element={<Compare />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/membership" element={<Membership />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/procedures" element={<Procedures />} />
                    <Route path="/procedures/:slug" element={<ProcedureDetail />} />
                    <Route path="/news" element={<News />} />
                    <Route path="/news/:slug" element={<NewsDetail />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/brand" element={<Brand />} />
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <Admin />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/suspended"
                      element={
                        <ProtectedRoute>
                          <Suspended />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route
                      path="/onboarding"
                      element={
                        <ProtectedRoute>
                          <Onboarding />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </Layout>
            </AuthProvider>
          </ShortlistProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;

